import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CopyButton } from "@/components/animate-ui/components/buttons/copy";
import {
	type ScheduleConfig,
	TaskConfirmationModal,
	type TaskFormData,
	type WebhookConfig,
} from "@/components/task-confirmation-modal";
import { TaskFilters } from "@/components/teammate/task-filters";
import { TaskList } from "@/components/teammate/task-list";
import { TaskRunPanel } from "@/components/teammate/task-run-panel";
import { TeammateHeader } from "@/components/teammate/teammate-header";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
	useAgent,
	useAgentRuns,
	useCreateSchedule,
	useCreateTask,
	useDeleteSchedule,
	useRotateTaskWebhook,
	useRunTask,
	useSchedules,
	useTasks,
	useUpdateTask,
	useUpdateTaskWebhook,
} from "@/lib/api/hooks/tasks";
import type { components } from "@/lib/api/types";
import { useAuth } from "@/lib/auth/hooks";
import { buildMetaTags } from "@/lib/seo/meta";
import { useBubbleStream } from "@/lib/streaming/use-bubble-stream";
import { buildScheduleParams } from "@/lib/utils/schedule";
import { useTeammateUIStore } from "@/stores/teammate-ui-store";

export const Route = createFileRoute("/_dashboard/teammates/$id")({
	head: () => {
		const meta = buildMetaTags({
			title: "Teammate",
			description: "Manage your teammate and their tasks",
			noIndex: true,
			noFollow: true,
		});

		return { meta };
	},

	component: TeammatePage,
});

function TeammatePage() {
	const { id } = Route.useParams();
	const agentId = Number.parseInt(id, 10);
	const navigate = useNavigate();
	const { accessToken } = useAuth();

	// Fetch the specific agent
	const { data: agent, isLoading: isLoadingAgent, error: agentError } = useAgent(agentId);
	const { data: allTasks, isLoading: isLoadingTasks } = useTasks();
	const { data: schedulesData } = useSchedules();
	const schedules = schedulesData?.schedules || [];
	const { data: agentRuns = [] } = useAgentRuns(agentId);

	// Get filter state and panel state
	const {
		searchQuery,
		statusFilter,
		typeFilter,
		sortOption,
		selectedTaskId,
		isPanelOpen,
		setSelectedTask,
		closePanel,
	} = useTeammateUIStore();

	// Task creation/edit dialog state
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [taskInstructions, setTaskInstructions] = useState("");
	const [editingTaskId, setEditingTaskId] = useState<number | null>(null);
	const [defaultName, setDefaultName] = useState("");
	const [defaultGoals, setDefaultGoals] = useState("");
	const [defaultIntegrations, setDefaultIntegrations] = useState<number[]>([]);
	const [defaultSchedule, setDefaultSchedule] = useState<ScheduleConfig | undefined>(undefined);
	const [defaultWebhook, setDefaultWebhook] = useState<WebhookConfig | undefined>(undefined);
	const [defaultPermissionMode, setDefaultPermissionMode] = useState<"yolo" | "safe" | "plan">(
		"safe"
	);
	const [webhookUrlDialog, setWebhookUrlDialog] = useState<string | null>(null);
	const [isWebhookDialogOpen, setIsWebhookDialogOpen] = useState(false);

	// API mutations
	const createTask = useCreateTask();
	const updateTask = useUpdateTask(editingTaskId || 0);
	const createSchedule = useCreateSchedule();
	const deleteSchedule = useDeleteSchedule();
	const runTask = useRunTask();
	const updateTaskWebhook = useUpdateTaskWebhook();
	const rotateTaskWebhook = useRotateTaskWebhook();

	// Stream handler for "run now" execution
	const { start: startExecution, runId, isStreaming } = useBubbleStream();

	// Navigate to run view when runId is available
	useEffect(() => {
		if (runId && isStreaming) {
			navigate({ to: "/runs/$runId", params: { runId: String(runId) } });
		}
	}, [runId, isStreaming, navigate]);

	// Filter and sort tasks
	const tasks = useMemo(() => {
		if (!allTasks) return [];

		// Filter by agent
		let filtered = allTasks.filter((t) => t.agent_instance_id === agentId);

		// Apply search
		if (searchQuery) {
			const query = searchQuery.toLowerCase();
			filtered = filtered.filter(
				(t) => t.name.toLowerCase().includes(query) || t.instructions?.toLowerCase().includes(query)
			);
		}

		// Apply status filter (UI uses "paused" but API status is "disabled")
		if (statusFilter !== "all") {
			const mappedFilter = statusFilter === "paused" ? "disabled" : statusFilter;
			filtered = filtered.filter((t) => t.status.toLowerCase() === mappedFilter);
		}

		// Apply type filter
		if (typeFilter !== "all") {
			filtered = filtered.filter((t) => {
				if (typeFilter === "scheduled") {
					// Task is scheduled if it has any schedules
					return schedules.some((s) => s.task_id === t.id);
				}
				if (typeFilter === "manual") {
					// On-demand task has no schedule and no webhook trigger enabled
					return !schedules.some((s) => s.task_id === t.id) && !t.webhook_enabled;
				}
				if (typeFilter === "triggered") {
					// Triggered tasks are webhook-enabled and unscheduled
					return t.webhook_enabled && !schedules.some((s) => s.task_id === t.id);
				}
				return true;
			});
		}

		// Apply sorting
		filtered = [...filtered].sort((a, b) => {
			switch (sortOption) {
				case "name-az":
					return a.name.localeCompare(b.name);
				case "name-za":
					return b.name.localeCompare(a.name);
				case "last-run":
					// Sort by updated_at (most recent first)
					return (
						(b.updated_at ? new Date(b.updated_at).getTime() : 0) -
						(a.updated_at ? new Date(a.updated_at).getTime() : 0)
					);
				case "next-run": {
					const getNextRun = (id: number) => {
						const s = schedules.find((s) => s.task_id === id);
						return s?.next_run_time ? new Date(s.next_run_time).getTime() : Number.MAX_SAFE_INTEGER;
					};
					return getNextRun(a.id) - getNextRun(b.id);
				}
				default:
					return 0;
			}
		});

		return filtered;
	}, [allTasks, agentId, searchQuery, statusFilter, typeFilter, sortOption, schedules]);

	const latestRunByTaskId = useMemo(() => {
		const map = new Map<number, components["schemas"]["RunResponse"]>();

		for (const run of agentRuns ?? []) {
			const taskId = run.task_id;
			if (typeof taskId !== "number") {
				continue;
			}

			const existing = map.get(taskId);
			if (!existing) {
				map.set(taskId, run);
				continue;
			}

			const existingTime = existing.created_at ? new Date(existing.created_at).getTime() : NaN;
			const runTime = run.created_at ? new Date(run.created_at).getTime() : NaN;

			if (!Number.isNaN(runTime) && (Number.isNaN(existingTime) || runTime > existingTime)) {
				map.set(taskId, run);
			}
		}

		return map;
	}, [agentRuns]);

	const buildDefaultWebhookConfig = (
		task: components["schemas"]["TaskResponse"]
	): WebhookConfig => ({
		enabled: task.webhook_enabled ?? false,
		hasToken: task.webhook_has_token ?? false,
		tokenPrefix: task.webhook_token_prefix ?? null,
		maskedUrl: null,
	});

	// Loading state
	if (isLoadingAgent || isLoadingTasks) {
		return (
			<div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 max-w-6xl">
				<div className="space-y-4 sm:space-y-6">
					<Card className="p-6">
						<div className="flex items-start gap-4">
							<Skeleton className="h-16 w-16 rounded-full" />
							<div className="flex-1 space-y-2">
								<Skeleton className="h-8 w-48" />
								<Skeleton className="h-4 w-32" />
								<Skeleton className="h-4 w-24" />
							</div>
						</div>
					</Card>

					<div className="space-y-3">
						<Skeleton className="h-24 w-full" />
						<Skeleton className="h-24 w-full" />
						<Skeleton className="h-24 w-full" />
					</div>
				</div>
			</div>
		);
	}

	// Error state
	if (agentError) {
		return (
			<div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 max-w-6xl">
				<div className="flex items-center justify-center min-h-[400px]">
					<div className="text-center space-y-2">
						<h2 className="text-2xl font-semibold">Teammate not found</h2>
						<p className="text-muted-foreground">
							This teammate doesn't exist or you don't have access to it.
						</p>
					</div>
				</div>
			</div>
		);
	}

	// Agent still loading but no error
	if (!agent) {
		return null;
	}

	// Handle new task button click
	const handleNewTask = () => {
		// Check if agent is enabled
		if (agent.status !== "enabled") {
			toast.error("Cannot create tasks for disabled or archived agents");
			return;
		}
		setEditingTaskId(null);
		setTaskInstructions("");
		setDefaultName("");
		setDefaultGoals("");
		setDefaultIntegrations([]);
		setDefaultSchedule(undefined);
		setDefaultWebhook(undefined);
		setDefaultPermissionMode("yolo");
		setIsModalOpen(true);
	};

	// Handle edit task button click
	const handleEditTask = (taskId: number) => {
		if (agent.status !== "enabled") {
			toast.error("Cannot edit tasks for disabled or archived agents");
			return;
		}
		const task = allTasks?.find((t) => t.id === taskId);
		if (!task) return;

		setEditingTaskId(taskId);
		setTaskInstructions(task.instructions);
		setDefaultName(task.name);
		setDefaultGoals(task.goals || "");
		setDefaultIntegrations(task.integration_ids || []);
		setDefaultPermissionMode((task.permission_mode as "yolo" | "safe" | "plan") || "yolo");
		setDefaultWebhook(buildDefaultWebhookConfig(task));

		// Find schedule for this task if it exists
		const existingSchedule = schedules.find((s) => s.task_id === taskId);
		if (existingSchedule) {
			setDefaultSchedule({
				triggerType: existingSchedule.trigger_type === "interval" ? "interval" : "cron",
				intervalSeconds: existingSchedule.interval_seconds ?? undefined,
				cronExpression: existingSchedule.cron_expression ?? undefined,
				timezone: existingSchedule.timezone ?? "UTC",
			});
		} else {
			setDefaultSchedule(undefined);
		}

		setIsModalOpen(true);
	};

	// Handle schedule task — opens modal with schedule pre-selected
	const handleScheduleTask = (taskId: number) => {
		if (agent.status !== "enabled") {
			toast.error("Cannot schedule tasks for disabled or archived agents");
			return;
		}
		const task = allTasks?.find((t) => t.id === taskId);
		if (!task) return;

		setEditingTaskId(taskId);
		setTaskInstructions(task.instructions);
		setDefaultName(task.name);
		setDefaultGoals(task.goals || "");
		setDefaultIntegrations(task.integration_ids || []);
		setDefaultPermissionMode((task.permission_mode as "yolo" | "safe" | "plan") || "yolo");
		setDefaultWebhook(buildDefaultWebhookConfig(task));

		const existingSchedule = schedules.find((s) => s.task_id === taskId);
		if (existingSchedule) {
			setDefaultSchedule({
				triggerType: existingSchedule.trigger_type === "interval" ? "interval" : "cron",
				intervalSeconds: existingSchedule.interval_seconds ?? undefined,
				cronExpression: existingSchedule.cron_expression ?? undefined,
				timezone: existingSchedule.timezone ?? "UTC",
			});
		} else {
			setDefaultSchedule({
				triggerType: "cron",
				cronExpression: "0 9 * * *",
				timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			});
		}

		setIsModalOpen(true);
	};

	// Handle running an existing task
	const handleRunTask = async (taskId: number) => {
		if (agent.status !== "enabled") {
			toast.error("Cannot run tasks for disabled or archived agents");
			return;
		}

		const task = allTasks?.find((t) => t.id === taskId);
		if (!task) {
			toast.error("Task not found");
			return;
		}

		if (task.status === "disabled") {
			toast.error("Cannot run paused tasks. Please resume the task first.");
			return;
		}

		if (task.status === "archived") {
			toast.error("Cannot run archived tasks");
			return;
		}

		runTask.mutate(taskId, {
			onSuccess: ({ runId }) => {
				toast.success("Task is running", {
					description: task.name,
					action: {
						label: "View run",
						onClick: () => navigate({ to: "/runs/$runId", params: { runId: String(runId) } }),
					},
					duration: 10000,
				});
			},
			onError: (error) => {
				toast.error("Failed to start task", { description: error.message });
			},
		});
	};

	const rotateWebhookForTask = async (taskId: number) => {
		return rotateTaskWebhook.mutateAsync(taskId);
	};

	const handleRotateWebhookFromModal = async () => {
		if (!editingTaskId) {
			throw new Error("No task selected");
		}

		const rotated = await rotateWebhookForTask(editingTaskId);
		setWebhookUrlDialog(rotated.webhook_url);
		setIsWebhookDialogOpen(true);
		return {
			webhookUrl: rotated.webhook_url,
			maskedUrl: rotated.masked_url,
		};
	};

	// Handle task creation/update submission
	const handleSubmitTask = async (formData: TaskFormData) => {
		if (agent.status !== "enabled") {
			toast.error("Cannot create or execute tasks for disabled or archived agents");
			return;
		}

		try {
			let taskId: number;
			const existingTask =
				editingTaskId !== null ? allTasks?.find((t) => t.id === editingTaskId) : null;

			if (editingTaskId) {
				// Update existing task
				await updateTask.mutateAsync({
					name: formData.name,
					instructions: formData.instructions,
					expected_output: formData.expectedOutput || null,
					goals: formData.goals || null,
					integration_ids: formData.integrations.length > 0 ? formData.integrations : null,
					email_notifications: formData.emailNotifications,
					permission_mode: formData.permissionMode,
				});
				taskId = editingTaskId;
			} else {
				// Create new task
				const task = await createTask.mutateAsync({
					agent_instance_id: agentId,
					name: formData.name,
					instructions: formData.instructions,
					expected_output: formData.expectedOutput || null,
					goals: formData.goals || null,
					integration_ids: formData.integrations.length > 0 ? formData.integrations : null,
					email_notifications: formData.emailNotifications,
					permission_mode: formData.permissionMode,
				});
				taskId = task.id;
			}

			// Delete old schedule if it exists (handles schedule changes and trigger mode switches)
			if (editingTaskId) {
				const existingSchedule = schedules.find((s) => s.task_id === editingTaskId);
				if (existingSchedule) {
					const scheduleId = Number.parseInt(existingSchedule.job_id.replace("schedule_", ""), 10);
					await deleteSchedule.mutateAsync(scheduleId);
				}
			}

			// Enforce mutually exclusive trigger modes: non-external disables webhook
			if (editingTaskId && formData.schedule !== "external" && existingTask?.webhook_enabled) {
				await updateTaskWebhook.mutateAsync({
					taskId: editingTaskId,
					updates: { enabled: false },
				});
			}

			// Handle run now, webhook trigger, or scheduled modes
			if (formData.schedule === "now") {
				setIsModalOpen(false);

				// In edit mode, just save without executing
				if (editingTaskId) {
					toast.success("Task updated successfully!");
				} else {
					// In create mode, execute immediately
					toast.success("Task created! Starting execution...");

					await startExecution({
						instructions: formData.instructions,
						requestBody: {
							instructions: formData.instructions,
							expected_output: formData.expectedOutput || null,
							goals: formData.goals || null,
							integration_ids: formData.integrations.length > 0 ? formData.integrations : [],
							email_notifications: formData.emailNotifications,
							inbound_email_enabled: formData.inboundEmailEnabled,
							permission_mode: formData.permissionMode,
							schedule: "now",
							timezone: formData.timezone || "UTC",
						},
						accessToken: accessToken || "",
					});
				}
			} else if (formData.schedule === "external") {
				let createdWebhookUrl: string | null = null;
				const needsRotation = !editingTaskId || !existingTask?.webhook_has_token;

				if (needsRotation) {
					const rotated = await rotateWebhookForTask(taskId);
					createdWebhookUrl = rotated.webhook_url;
				} else {
					await updateTaskWebhook.mutateAsync({
						taskId,
						updates: { enabled: true },
					});
				}

				setIsModalOpen(false);
				if (createdWebhookUrl) {
					setWebhookUrlDialog(createdWebhookUrl);
					setIsWebhookDialogOpen(true);
				}
				toast.success(
					editingTaskId
						? "Task updated and webhook configured!"
						: "Task created and webhook configured!"
				);
			} else {
				// Create new schedule
				const { cronExpression, intervalSeconds } = buildScheduleParams(formData);

				await createSchedule.mutateAsync({
					task_id: taskId,
					agent_instance_id: agentId,
					cron_expression: cronExpression,
					interval_seconds: intervalSeconds,
					run_date: null,
					timezone: formData.timezone || "UTC",
				});

				setIsModalOpen(false);
				toast.success(
					editingTaskId ? "Task updated successfully!" : "Task created and scheduled successfully!"
				);
			}

			setEditingTaskId(null);
		} catch (error) {
			console.error("Failed to save task:", error);
			toast.error(
				editingTaskId
					? "Failed to update task. Please try again."
					: "Failed to create task. Please try again."
			);
		}
	};

	return (
		<div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 max-w-6xl">
			<div className="space-y-4 sm:space-y-6">
				<TeammateHeader agent={agent} taskCount={tasks.length} />

				<Card className="p-4 sm:p-6">
					<div className="space-y-4 sm:space-y-6">
						<div className="space-y-3 sm:space-y-4">
							<TaskFilters onNewTask={handleNewTask} disabled={agent.status !== "enabled"} />
							<TaskList
								tasks={tasks}
								onEdit={handleEditTask}
								onSelect={setSelectedTask}
								onRunNow={handleRunTask}
								onSchedule={handleScheduleTask}
								latestRunByTaskId={latestRunByTaskId}
							/>
						</div>
					</div>
				</Card>
			</div>

			{/* Task creation/edit modal */}
			<TaskConfirmationModal
				open={isModalOpen}
				onOpenChange={setIsModalOpen}
				instructions={taskInstructions}
				onSubmit={handleSubmitTask}
				isSubmitting={
					createTask.isPending ||
					updateTask.isPending ||
					createSchedule.isPending ||
					deleteSchedule.isPending ||
					updateTaskWebhook.isPending ||
					rotateTaskWebhook.isPending
				}
				isEditMode={editingTaskId !== null}
				defaultName={defaultName}
				defaultIntegrations={defaultIntegrations}
				defaultGoals={defaultGoals}
				showInstructionsField={true}
				defaultSchedule={defaultSchedule}
				defaultWebhook={defaultWebhook}
				defaultPermissionMode={defaultPermissionMode}
				onRotateWebhook={handleRotateWebhookFromModal}
			/>

			<Dialog
				open={isWebhookDialogOpen}
				onOpenChange={(open) => {
					setIsWebhookDialogOpen(open);
					if (!open) {
						setWebhookUrlDialog(null);
					}
				}}
			>
				<DialogContent className="max-w-xl">
					<DialogHeader>
						<DialogTitle>Webhook URL Created</DialogTitle>
						<DialogDescription>
							Make a POST request to this URL with an optional JSON payload to trigger this task.
							The full URL is shown only once — copy it now.
						</DialogDescription>
					</DialogHeader>
					{webhookUrlDialog && (
						<div className="rounded-md border bg-muted/40 p-3 flex items-start justify-between gap-3">
							<code className="text-xs break-all">{webhookUrlDialog}</code>
							<CopyButton content={webhookUrlDialog} variant="outline" size="sm" />
						</div>
					)}
					<DialogFooter>
						<Button type="button" onClick={() => setIsWebhookDialogOpen(false)}>
							Close
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Task run panel */}
			<TaskRunPanel
				open={isPanelOpen}
				onOpenChange={(open) => {
					if (!open) closePanel();
				}}
				task={allTasks?.find((t) => t.id === selectedTaskId) ?? null}
				onRunNow={handleRunTask}
				onEdit={handleEditTask}
				onSchedule={handleScheduleTask}
				latestRun={selectedTaskId ? latestRunByTaskId.get(selectedTaskId) : undefined}
			/>
		</div>
	);
}
